/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type FormEvent, type ReactElement, useState } from 'react';
import { Form } from 'react-router';
import { useTranslation } from 'react-i18next';
import { REGEXP_ONLY_DIGITS } from 'input-otp';
import { Link } from '@/components/link';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { FormSubmitButton } from '@/components/buttons/form-submit-button';
import { routes } from '@/route-paths';
import type { ValidationMethod } from '@/extensions/priceshoes/lib/auth/validation-method';
import {
    VERIFICATION_CODE_LENGTH,
    type VerificationCodeError,
    validateVerificationCode,
} from '@/extensions/priceshoes/lib/auth/verification-code';

interface VerificationCodeFormProps {
    /** Partner ID or email of the shopper validating their identity. */
    identifier: string;
    /** Validation methods of the shopper, kept to go back to the method selection. */
    validations: ValidationMethod[];
    /** Identity validation in progress, encrypted by the server; posted back as is. */
    identityFlow: string;
    /** Where the code was sent. */
    method: 'email' | 'phone';
    /** Error resolved by the login action. */
    error?: string;
    /** The code was just resent: shows a confirmation. */
    resent?: boolean;
    /** The attempts for this code ran out (CA8): it can't be validated anymore, but a new code can be requested. */
    locked?: boolean;
    returnUrl?: string | null;
}

/**
 * Hidden fields every form of this screen posts back, so the login action can show it again
 * (the identifier and the masked methods are the shopper's own data; the flow goes encrypted).
 */
function StepFields({
    identifier,
    validations,
    identityFlow,
    method,
    returnUrl,
}: Pick<
    VerificationCodeFormProps,
    'identifier' | 'validations' | 'identityFlow' | 'method' | 'returnUrl'
>): ReactElement {
    return (
        <>
            <input type="hidden" name="identifier" value={identifier} />
            <input type="hidden" name="validations" value={JSON.stringify(validations)} />
            <input type="hidden" name="identityFlow" value={identityFlow} />
            <input type="hidden" name="validationMethod" value={method} />
            {returnUrl ? <input type="hidden" name="returnUrl" value={returnUrl} /> : null}
        </>
    );
}

/**
 * Verification code screen (PRIC-168): the shopper enters the 6-digit code Auth Extension sent to their
 * email or phone. Allows resending the code to the same medium and choosing another method.
 */
export default function VerificationCodeForm({
    identifier,
    validations,
    identityFlow,
    method,
    error,
    resent = false,
    locked = false,
    returnUrl,
}: VerificationCodeFormProps): ReactElement {
    const { t } = useTranslation('extPriceshoes');
    const [code, setCode] = useState('');
    const [codeError, setCodeError] = useState<VerificationCodeError | null>(null);
    const maskedValue = validations.find(({ type }) => type === method)?.value ?? '';
    const stepFields = { identifier, validations, identityFlow, method, returnUrl };

    // Validate the format before submitting; the action validates again. Once submitted the field is cleared
    // (the form data is already taken), so a wrong code can be typed again right away (CA6).
    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        const validationError = validateVerificationCode(code);
        setCodeError(validationError);
        if (validationError) {
            event.preventDefault();
            return;
        }
        setCode('');
    };

    const fieldError = codeError ? t(`login.verification.errors.${codeError}`) : error;

    return (
        <div className="space-y-6">
            <div className="space-y-2">
                <h1 className="text-2xl font-semibold text-foreground">{t('login.verification.title')}</h1>
                <p className="text-foreground">
                    {t(`login.verification.description.${method}`, { value: maskedValue })}
                </p>
            </div>

            {resent && !fieldError ? (
                <p role="status" className="text-sm text-foreground">
                    {t('login.verification.resent')}
                </p>
            ) : null}

            <Form method="post" className="space-y-6" noValidate onSubmit={handleSubmit}>
                <div className="space-y-2">
                    <Label htmlFor="otp">{t('login.verification.label')}</Label>
                    <InputOTP
                        id="otp"
                        name="otp"
                        maxLength={VERIFICATION_CODE_LENGTH}
                        pattern={REGEXP_ONLY_DIGITS}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        // oxlint-disable-next-line jsx-a11y/no-autofocus -- the code is the only input of this step
                        autoFocus
                        value={code}
                        disabled={locked}
                        onChange={(value) => {
                            setCode(value);
                            setCodeError(null);
                        }}
                        aria-invalid={fieldError ? true : undefined}
                        aria-describedby={fieldError ? 'otp-error' : undefined}>
                        <InputOTPGroup>
                            {Array.from({ length: VERIFICATION_CODE_LENGTH }, (_, index) => (
                                <InputOTPSlot key={index} index={index} aria-invalid={fieldError ? true : undefined} />
                            ))}
                        </InputOTPGroup>
                    </InputOTP>
                    {fieldError ? (
                        <p id="otp-error" role="alert" className="text-sm text-destructive">
                            {fieldError}
                        </p>
                    ) : null}
                </div>

                <StepFields {...stepFields} />
                <input type="hidden" name="loginMode" value="priceshoesVerificationCode" />

                <FormSubmitButton
                    defaultText={t('login.next')}
                    submittingText={t('login.submitting')}
                    disabled={locked}
                />
            </Form>

            {/* Resends the code to the same medium (CA7); also the way out once the code is locked. */}
            <Form method="post">
                <StepFields {...stepFields} />
                <input type="hidden" name="loginMode" value="priceshoesResendCode" />
                <Button type="submit" variant="link" className="w-full">
                    {t('login.verification.resend')}
                </Button>
            </Form>

            <div className="space-y-4 border-t border-border pt-6">
                {/* Identifies the shopper again, which shows the method selection with fresh methods. */}
                <Form method="post">
                    <input type="hidden" name="identifier" value={identifier} />
                    <input type="hidden" name="loginMode" value="identify" />
                    {returnUrl ? <input type="hidden" name="returnUrl" value={returnUrl} /> : null}
                    <Button type="submit" variant="outline" className="w-full">
                        {t('login.verification.otherMethod')}
                    </Button>
                </Form>
                <Button asChild variant="outline" className="w-full">
                    <Link to={routes.login}>{t('login.password.otherAccount')}</Link>
                </Button>
            </div>
        </div>
    );
}
